import React, {useState, useEffect} from 'react';
import {View, Text, Image, Pressable, Modal} from 'react-native';
import SignatureCanvas from 'react-signature-canvas';
import ReactSignatureCanvas from 'react-signature-canvas';
import Icon from '../Shared/Icon';
import {
  DynamicAttachment,
  DynamicSignatureProps,
} from '../../Types/ControlTypes';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import Colors from '../../Styles/Shared/Colors';
import {IAttachmentService} from '../../Services/Interfaces/IAttachmentService';
import AttachmentService from '../../Services/AttachmentService';

const DynamicSignature = (props: DynamicSignatureProps): React.ReactElement => {
  const [value, setValue] = useState(props.value ?? '');
  const [sasUri, setSasUri] = useState('');
  const [error, setError] = useState('');

  const [isShowingModal, setIsShowingModal] = useState(false);
  const [modalError, setModalError] = useState('');

  const attachmentService: IAttachmentService = new AttachmentService(() => {});

  const signatureRef = React.createRef<ReactSignatureCanvas>();

  useEffect(() => {
    try {
      if (value) {
        const getSasUri = async () => {
          let newSasUri = '';

          let attachment = JSON.parse(value) as DynamicAttachment;
          if (attachment?.blobUri)
            newSasUri = await attachmentService.getSasUri(attachment!.blobUri!);

          setSasUri(newSasUri);
        };

        getSasUri();
      }
    } catch (error) {
      console.log('Error: Bad Signature value serialization');
    }
  }, []);

  useEffect(() => {
    if (props.onChange)
      props.onChange(
        props.controlId,
        props.controlTypeId,
        value,
        valid(),
        null,
        null,
      );
  }, [value]);

  useEffect(() => {
    if (props.showError) valid();
  }, [props.showError]);

  function showModal(): void {
    if (props.disabled) return;

    setIsShowingModal(true);
    setModalError('');
  }

  function hideModal(): void {
    setIsShowingModal(false);
  }

  function clearSignature(): void {
    if (signatureRef.current) signatureRef.current.clear();
  }

  async function saveSignature(): Promise<void> {
    let signature = '';
    if (signatureRef.current)
      signature = signatureRef.current.getCanvas().toDataURL();

    // Validations
    if (signatureRef.current && signatureRef.current.isEmpty()) {
      setModalError('Signature cannot be empty');
      return;
    }

    props.setIsLoading(true);
    setIsShowingModal(false);

    var file = attachmentService.base64toFile(
      signature,
      Math.floor(Date.now() / 1000).toString(),
    );

    let signatureImageUri = await attachmentService.pushAttachmentChunks(
      props.submissionId,
      props.attachmentsType,
      file.name,
      file,
    );

    var attachmentSignature = {
      refId: props.submissionId,
      refType: props.attachmentsType,
      controlId: props.controlId,
      fileId: props.submissionId,
      uri: '',
      name: file.name,
      type: file.type,
      size: file.size,
      blobUri: signatureImageUri,
    } as DynamicAttachment;

    let newSasUri = await attachmentService.getSasUri(signatureImageUri);
    setSasUri(newSasUri);

    await removeOldSignature();

    setValue(JSON.stringify(attachmentSignature));

    props.setIsLoading(false);
  }

  async function removeOldSignature(): Promise<void> {
    let attachment: DynamicAttachment | undefined = undefined;
    try {
      attachment = JSON.parse(value) as DynamicAttachment;
    } catch (error) {}

    if (attachment?.blobUri)
      await attachmentService.deleteBlobAttachment(attachment.blobUri);
  }

  function valid(): boolean {
    let isValid = true;
    let error = '';

    if (props.config?.required && value === '') {
      isValid = false;
      error =
        (props.label ? '"' + props.label + '"' : 'This field') + ' is required';
    }

    if (props.showError) setError(error);

    return isValid;
  }

  const renderModal = (): React.ReactElement => {
    return (
      <Modal
        visible={isShowingModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType="fade">
        <View style={ControlsStyleSheet.modalBackground}>
          <View
            style={[ControlsStyleSheet.groupSelectorModal, {width: 440, height: 'auto'}]}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                {props.label}
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={hideModal}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            <View>
              <View style={{marginTop: 12}}>
                <Text style={ControlsStyleSheet.label}>Signature</Text>
                <View style={{width: 400}}>
                  <View
                    style={{
                      backgroundColor: Colors.white,
                      borderColor: Colors.darkGreen,
                      borderWidth: 1,
                      borderRadius: 20,
                    }}>
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{height: 240}}
                    />
                  </View>
                  <View
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      backgroundColor: Colors.white,
                      borderRadius: 6,
                    }}>
                    <Pressable
                      style={({pressed}) => [
                        ControlsStyleSheet.autocompleteButton,
                        pressed && {
                          backgroundColor: Colors.darkGreenTransparent,
                        },
                      ]}
                      onPress={clearSignature}>
                      <Text style={ControlsStyleSheet.autocompleteButtonText}>
                        Clear
                      </Text>
                    </Pressable>
                  </View>
                  <View
                    style={{
                      position: 'absolute',
                      right: 8,
                      bottom: 8,
                    }}>
                    <Pressable
                      style={({pressed}) => [
                        ControlsStyleSheet.linkRecordAddButton,
                        pressed && {
                          backgroundColor: Colors.darkGreenTransparent,
                        },
                      ]}
                      disabled={props.disabled}
                      onPress={saveSignature}>
                      <Icon
                        icon={'save'}
                        color={Colors.darkestGreen}
                        size={24}
                      />
                    </Pressable>
                  </View>
                </View>
                <Text style={ControlsStyleSheet.error}>{modalError}</Text>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <View style={{display: props.visible === false ? 'none' : 'flex'}}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 24,
        }}>
        <Text style={ControlsStyleSheet.labelLarge}>
          {props.label}
          <Text
            style={[
              ControlsStyleSheet.required,
              {fontSize: 16, verticalAlign: 'middle'},
            ]}>
            {props.config?.required ? '*' : ''}
          </Text>
        </Text>
      </View>
      {renderModal()}
      {sasUri ? (
        <View>
          <View
            style={{
              width: 400,
              backgroundColor: 'rgba(32, 55, 51, 0.05)',
              borderColor: Colors.white,
              borderWidth: 1,
              borderRadius: 20,
            }}>
            <Image
              resizeMode={'contain'}
              style={{width: '100%', height: 240}}
              source={{uri: sasUri}}
            />{' '}
            {!props.disabled && (
              <Pressable
                style={({pressed}) => [
                  {position: 'absolute', right: 8, bottom: 8},
                  ControlsStyleSheet.linkRecordAddButton,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                  },
                ]}
                disabled={props.disabled}
                onPress={showModal}>
                <Icon icon={'pencil'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            )}
          </View>
        </View>
      ) : (
        <View
          style={{
            width: 400,
            height: 240,
            backgroundColor: 'rgba(32, 55, 51, 0.05)',
            borderColor: Colors.white,
            borderWidth: 1,
            borderRadius: 20,
          }}>
          {!props.disabled && (
            <Pressable
              style={({pressed}) => [
                {position: 'absolute', right: 8, bottom: 8},
                ControlsStyleSheet.linkRecordAddButton,
                pressed && {
                  backgroundColor: Colors.darkGreenTransparent,
                },
              ]}
              disabled={props.disabled}
              onPress={showModal}>
              <Icon icon={'pencil'} color={Colors.darkestGreen} size={24} />
            </Pressable>
          )}
        </View>
      )}
      <Text style={ControlsStyleSheet.error}>{error}</Text>
    </View>
  );
};

export default DynamicSignature;
